/**
 * GLM診断統計量の計算ユーティリティ
 *
 * Leverage、Cook's distance、標準化残差などを計算する
 */

import type { GLMFamily, TrainedModel } from '../../types/model.types';
import type { PrimaryDataSet, DerivedDataSet, ColumnSchema, DataValue, ColumnId } from '../../types';
import { createDatasetId, createColumnId, createTransformOperationId, createColumnName } from '../../types/id.types';
import { markAsTransient } from '../../types/serialization.types';
import { invertMatrix } from './glm';
import logger from './logger';

/**
 * Leverage (hat values) を計算する
 *
 * 効率的な実装: H全体ではなく対角要素のみを計算
 * h_i = x_i' (X'WX)^(-1) x_i * w_i
 *
 * @param X デザイン行列 (n x p) - row-major形式のFlat配列
 * @param W 最終イテレーションでの重み (n)
 * @param n 行数
 * @param p 列数
 * @returns leverage値の配列 (n)
 */
export function computeLeverage(X: Float64Array, W: number[], n: number, p: number): number[] {
  // X'WX を計算
  const XtWX: number[][] = [];
  for (let i = 0; i < p; i++) {
    XtWX[i] = [];
    for (let j = 0; j < p; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += X[k * p + i] * W[k] * X[k * p + j];
      }
      XtWX[i][j] = sum;
    }
  }

  // (X'WX)^(-1) を計算
  let XtWX_inv: number[][];
  try {
    XtWX_inv = invertMatrix(XtWX);
  } catch {
    logger.warn('[GLMDiagnostics] Failed to invert X\'WX matrix, using zeros for leverage');
    return new Array(n).fill(0);
  }

  // 各観測値についてh_iを計算
  const leverage: number[] = [];
  for (let i = 0; i < n; i++) {
    // x_i' (X'WX)^(-1) x_i を計算
    let h = 0;
    for (let j = 0; j < p; j++) {
      for (let k = 0; k < p; k++) {
        h += X[i * p + j] * XtWX_inv[j][k] * X[i * p + k];
      }
    }
    h *= W[i];

    // 数値的安定性のため0-1の範囲にクランプ
    leverage[i] = Math.max(0, Math.min(1, h));
  }

  return leverage;
}

/**
 * Cook's distance を計算する
 *
 * D_i = (r_i^2 / p) * (h_i / (1 - h_i)^2)
 *
 * @param standardizedResiduals 標準化残差 (n)
 * @param leverage leverage値 (n)
 * @param p 予測変数の数
 * @returns Cook's distance の配列 (n)
 */
export function computeCooksDistance(
  standardizedResiduals: number[],
  leverage: number[],
  p: number
): number[] {
  const n = standardizedResiduals.length;
  const cooksDistance: number[] = [];

  for (let i = 0; i < n; i++) {
    const r = standardizedResiduals[i];
    const h = leverage[i];

    // (1 - h)が0に近い場合は0とする
    if (h >= 0.999) {
      cooksDistance[i] = 0;
    } else {
      const numerator = (r * r) / p;
      const denominator = (1 - h) * (1 - h);
      cooksDistance[i] = numerator * (h / denominator);
    }
  }

  return cooksDistance;
}

/**
 * 標準化残差を計算する
 *
 * r_i^std = r_i / sqrt(φ(1 - h_i))
 *
 * @param residuals 残差（DevianceまたはPearson） (n)
 * @param leverage leverage値 (n)
 * @param dispersionParameter dispersion parameter (φ)
 * @returns 標準化残差の配列 (n)
 */
export function computeStandardizedResiduals(
  residuals: number[],
  leverage: number[],
  dispersionParameter: number
): number[] {
  const n = residuals.length;
  const standardizedResiduals: number[] = [];

  for (let i = 0; i < n; i++) {
    const r = residuals[i];
    const h = leverage[i];

    // sqrt(φ(1 - h_i))
    const denominator = Math.sqrt(dispersionParameter * (1 - h));

    if (denominator > 1e-10) {
      standardizedResiduals[i] = r / denominator;
    } else {
      // 分母が0に近い場合は0とする
      standardizedResiduals[i] = 0;
    }
  }

  return standardizedResiduals;
}

/**
 * Dispersion parameter を計算する
 *
 * φ = Residual Deviance / df
 *
 * @param deviance residual deviance
 * @param df 自由度 (n - p)
 * @returns dispersion parameter
 */
export function computeDispersionParameter(deviance: number, df: number): number {
  if (df <= 0) {
    logger.warn('[GLMDiagnostics] Invalid degrees of freedom for dispersion parameter');
    return 1.0;
  }
  return deviance / df;
}

/**
 * Pearson Chi-square を計算する
 *
 * χ² = Σ(r_i^2) where r_i is Pearson residual
 *
 * @param pearsonResiduals Pearson残差 (n)
 * @returns Pearson Chi-square
 */
export function computePearsonChi2(pearsonResiduals: number[]): number {
  return pearsonResiduals.reduce((sum, r) => sum + r * r, 0);
}

/**
 * Cook's distance 等高線を計算する
 *
 * D = c を満たす点の軌跡
 * r = ±sqrt((c * p * (1 - h)^2) / h)
 *
 * @param c Cook's distance の値（例: 0.5, 1.0）
 * @param p 予測変数の数
 * @param maxLeverage 最大leverage値
 * @param numPoints 等高線の点数
 * @returns 等高線の点の配列 [{h, r}]
 */
export function computeCookDistanceContour(
  c: number,
  p: number,
  maxLeverage: number,
  numPoints: number = 100
): Array<{ h: number; r: number }> {
  const contour: Array<{ h: number; r: number }> = [];

  // hを0からmaxLeverageまで変化させる
  const hMin = 0.001; // 0除算を避ける
  const hMax = Math.min(0.999, maxLeverage); // 1に近い値を避ける

  for (let i = 0; i < numPoints; i++) {
    const h = hMin + (hMax - hMin) * (i / (numPoints - 1));

    // r = sqrt((c * p * (1 - h)^2) / h)
    const numerator = c * p * (1 - h) * (1 - h);
    const r = Math.sqrt(numerator / h);

    contour.push({ h, r });
  }

  return contour;
}

/**
 * Leverage閾値を計算する
 *
 * 高レバレッジの基準: h_i > 2p/n
 *
 * @param p 予測変数の数
 * @param n 観測数
 * @returns leverage閾値
 */
export function computeLeverageThreshold(p: number, n: number): number {
  return (2 * p) / n;
}

/**
 * Cook's distance閾値を計算する
 *
 * @param threshold 閾値（デフォルト: 0.5）
 * @returns Cook's distance閾値
 */
export function computeCooksDThreshold(threshold: number = 0.5): number {
  return threshold;
}

/**
 * Overdispersionを検出する
 *
 * φ > 1.5 の場合、overdispersionありと判定
 *
 * @param dispersionParameter dispersion parameter
 * @param family GLM family
 * @returns overdispersionの有無
 */
export function detectOverdispersion(
  dispersionParameter: number,
  family: GLMFamily
): boolean {
  // Gaussian以外でφ > 1.5ならoverdispersion
  if (family === 'poisson' || family === 'binomial') {
    return dispersionParameter > 1.5;
  }
  return false;
}

/**
 * 診断統計量を含むDerivedDataSetを作成する
 *
 * @param originalDataset 元のデータセット（PrimaryまたはDerived）
 * @param model 保存されたGLMモデル
 * @param diagnostics 診断統計量（必須）
 * @returns 診断統計量の列を追加したDerivedDataSet
 */
export async function createDiagnosticDataset(
  originalDataset: PrimaryDataSet | DerivedDataSet,
  model: TrainedModel,
  diagnostics: import('../../types/model.types').GLMDiagnosticStatistics
): Promise<DerivedDataSet> {
  logger.assert(model.type === 'glm', 'Model must be a GLM model');
  logger.assert(diagnostics != null, 'Diagnostics must be provided');
  logger.assert(diagnostics.incompleteObservations != null, 'Diagnostics must have incompleteObservations');

  // DerivedDataSetの場合、DerivedDataAccessorを使ってデータをロード
  let datasetWithData: PrimaryDataSet | (DerivedDataSet & { data: Record<ColumnId, DataValue[]>; rowCount: number });

  if (originalDataset.type === 'derived') {
    const { DerivedDataAccessor } = await import('./DerivedDataAccessor');
    const accessor = new DerivedDataAccessor(originalDataset);
    const loadedDataset = await accessor.ensureDataLoaded();

    logger.assert(loadedDataset.data != null, 'Failed to load data for derived dataset');
    logger.assert(loadedDataset.rowCount != null, 'Derived dataset must have rowCount after loading');

    datasetWithData = loadedDataset as DerivedDataSet & { data: Record<ColumnId, DataValue[]>; rowCount: number };
  } else {
    // PrimaryDataSetの場合、dataとrowCountは必ず存在する
    datasetWithData = originalDataset;
  }

  const diagnosticStats = diagnostics;

  // 元のデータセットの列をコピー
  const newColumns: ColumnSchema[] = [...originalDataset.schema.columns];
  const newData: Record<string, (string | number | boolean | null)[]> = {};

  // 元のデータをコピー
  for (const col of originalDataset.schema.columns) {
    newData[col.id] = [...datasetWithData.data[col.id]];
  }

  // incompleteObservationsのRowNumberをSetに変換（高速検索用）
  const incompleteRowNumbers = new Set(
    diagnosticStats.incompleteObservations.map(obs => obs.rowNumber)
  );

  // 元データセットのRowNumber列を取得
  const rowNumberColumn = datasetWithData.data[createColumnId('__system_rowid__')] as number[];

  // 診断統計量の列を展開（incomplete observationsにはnullを設定）
  const ratioScale = { scale: 'ratio' as const, inferredAutomatically: false };

  const diagnosticColumnsCompact: Array<{
    name: string;
    compactData: number[];  // complete observationsのみのデータ
  }> = [
    { name: 'fitted_values', compactData: diagnosticStats.fittedValues },
    { name: 'deviance_residuals', compactData: diagnosticStats.devianceResiduals },
    { name: 'pearson_residuals', compactData: diagnosticStats.pearsonResiduals },
    { name: 'standardized_residuals', compactData: diagnosticStats.standardizedResiduals },
    { name: 'leverage', compactData: diagnosticStats.leverage },
    { name: 'cooks_distance', compactData: diagnosticStats.cooksDistance }
  ];

  for (const { name, compactData } of diagnosticColumnsCompact) {
    const colId = createColumnId(name);
    newColumns.push({
      id: colId,
      name: createColumnName(name),
      type: 'float64',
      scale: ratioScale,
      nullable: true  // incomplete observationsはnull
    });

    // 元データの行数に展開
    const expandedData: (number | null)[] = new Array(datasetWithData.rowCount);
    let compactIndex = 0;

    for (let i = 0; i < datasetWithData.rowCount; i++) {
      const rowNumber = rowNumberColumn[i];

      if (incompleteRowNumbers.has(rowNumber)) {
        // Incomplete observation: nullを設定
        expandedData[i] = null;
      } else {
        // Complete observation: 診断統計量を設定
        expandedData[i] = compactData[compactIndex];
        compactIndex++;
      }
    }

    logger.assert(
      compactIndex === compactData.length,
      `All diagnostic values must be used: expected ${compactData.length}, used ${compactIndex}`
    );

    newData[colId] = expandedData;
  }

  // DerivedDataSetを作成
  const datasetId = createDatasetId(`${model.id}_diagnostics`);
  const operationId = createTransformOperationId(`glm_diagnostics_${model.id}`);
  const derivedDataset: DerivedDataSet = {
    id: datasetId,
    name: `${model.name} - Diagnostics`,
    type: 'derived',
    schema: {
      columns: newColumns
    },
    data: newData,
    rowCount: datasetWithData.rowCount,
    parentDatasetIds: [originalDataset.id],
    operation: {
      id: operationId,
      type: 'glm_diagnostics',
      modelId: model.id
    },
    metadata: {
      createdAt: new Date().toISOString(),
      description: `Diagnostic statistics for GLM model: ${model.name}`,
      basedOn: [originalDataset.id]
    }
  };

  // dataプロパティをtransientとしてマーク（MDSファイル保存時に除外）
  markAsTransient(derivedDataset, ['data']);

  return derivedDataset;
}
